import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { intFmt, SCHEDULE_EXT } from './scheduleUploadCard';
import { activityKey } from './quantityMatch';

// Schedule Baseline Review — the job's schedule as a scheduler reads it.
//
// One workspace, two panes on one vertical scroll: an activity grid on the left
// (id, cost code, description, dates, days, quantity, unit, float, and the
// logic that puts the activity where it is) and the Gantt those rows draw on
// the right. Between them they answer the only question this tab exists to
// settle — is this schedule good enough to baseline the job against?
//
// The grid is the scheduler's file, not an editor for it. Dates, durations and
// logic come from the P6/MSP/Excel export and are read-only here: editing them
// in Fusion would silently diverge from the schedule the field is actually
// working to. The two things that ARE Fusion's to own — which cost code an
// activity earns against, and how much quantity that code carries — stay
// editable, and write through the same mapping panel the Review modal shows.
//
// Float is derived, not imported. Most exports don't carry it, so a backward
// pass runs over the imported dates and the imported logic: an activity's late
// finish is the tightest its successors allow, and float is the slack between
// that and where it is actually scheduled. Everything downstream of it — the
// critical path, the "Critical only" filter, the negative-float issue chip — is
// that one number.

// ── palette ─────────────────────────────────────────────────────────────────

const INK = '#1B2027';
const INK_DIM = '#3A4756';
const MUTED = '#5B6675';
const FAINT = '#7C8899';
const GHOST = '#98A2B0';
const LINE = '#C8CFD8';
const RULE = '#EDF0F3';
const HEAD_BG = '#F2F4F7';
const SHELL = '#EDEFF2';
const ACCENT = '#0F6C8C';
const ACCENT_DK = '#0B506A';
const ACCENT_BG = '#E4F0F6';
const CRIT = '#C8402F';
const CRIT_BG = '#FBEDEB';
const CRIT_DK = '#9E2E20';
const WARN = '#B8791C';
const DATE_ISSUE = '#8E3AA8';
const GOOD = '#3D6B34';
const GOOD_BG = '#E8F0E6';

const MONO = "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace";

// ── grid geometry ───────────────────────────────────────────────────────────

const COL = {
    id: 46, code: 78, desc: 226, start: 96, finish: 96, days: 44, qty: 92, uom: 46, float: 58,
};
const GRID_W = Object.values(COL).reduce((a, b) => a + b, 0);      // 782
const LOGIC_W = 240;
const LOGIC_SHUT_W = 36;
const ROW_H = 30;
const BAR_H = 14;
const AXIS_H = 44;
const MAX_ROWS = 800;      // past this the grid is unreadable and slow

// Pixels per calendar day. The schedules in play work Saturdays, so the axis
// counts calendar days rather than a five-day week that would drop them.
const ZOOM = { day: 20, week: 5.4, month: 1.9 };

const SEQ_COLOR = {
    FS: ['#E4ECF3', '#2C5B7A'],
    SS: ['#E8F0E6', '#3D6B34'],
    FF: ['#F3ECDD', '#7A5A1E'],
    SF: ['#F1E6EE', '#6B2F5C'],
};

// ── helpers ─────────────────────────────────────────────────────────────────

const iso = (v) => (v ? moment(v).format('YYYY-MM-DD') : null);
const num = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : null;
};
const has = (v) => v !== null && v !== undefined && String(v).trim() !== '';

/** '18SS+8, 20FS' → [{ id, type, lag }]. A bare id is a finish-to-start link. */
const parseLinks = (s) => {
    if (!s) return [];
    return String(s).split(',').map((p) => p.trim()).filter(Boolean).map((p) => {
        const m = p.match(/^(.+?)\s*(FS|SS|FF|SF)?\s*([+-]\s*\d+(?:\.\d+)?)?$/i);
        if (!m) return null;
        return {
            id: m[1].trim(),
            type: (m[2] || 'FS').toUpperCase(),
            lag: m[3] ? parseFloat(m[3].replace(/\s/g, '')) : 0,
        };
    }).filter((x) => x && x.id);
};

const linkLabel = (l) => `${l.type}${l.lag > 0 ? `+${l.lag}` : l.lag < 0 ? l.lag : ''}`;
const linkStr = (list) => list.map((l) => `${l.id}${linkLabel(l)}`).join(', ');

const fmtDate = (d) => (d ? moment(d).format('MM/DD/YY') : '');
const longDate = (d) => (d ? moment(d).format('MMM D, YYYY') : '');

// ── the model ───────────────────────────────────────────────────────────────

/**
 * Activities → the rows the grid and the Gantt both draw, with hierarchy,
 * relationships and derived float attached.
 *
 * Hierarchy comes from whichever the export gave us, best first: an explicit
 * parent id (the sheet's Header ID), then the outline level, then the depth of
 * the WBS path. A row is a summary when something else hangs under it.
 */
const buildModel = (activities, contractISO) => {
    const src = (activities || []).filter((a) => a.Start_Date || a.Finish_Date);
    if (src.length === 0) return null;

    const rows = src.map((a, i) => {
        const start = iso(a.Start_Date) || iso(a.Finish_Date);
        const finish = iso(a.Finish_Date) || iso(a.Start_Date);
        return {
            key: String(a.Activity_Row_ID ?? i),
            id: String(a.Activity_Code ?? a.Activity_Row_ID ?? i),
            activity: a,
            desc: a.Activity_Name || a.Activity_Code || '',
            code: a.Cost_Code ? String(a.Cost_Code) : '',
            start,
            finish,
            days: num(a.Duration_Days),
            qty: num(a.Budget_Qty),
            uom: a.UOM || '',
            pipeSize: a.Pipe_Size || '',
            pct: num(a.Pct_Complete) || 0,
            milestone: a.Is_Milestone === true || a.Is_Milestone === 1,
            seq: has(a.Sequencing) ? String(a.Sequencing) : '',
            parent: has(a.Parent_Code) ? String(a.Parent_Code) : null,
            level: num(a.Outline_Level),
            wbs: a.WBS_Path || '',
            importedFloat: num(a.Total_Float),
            preds: parseLinks(a.Predecessors),
            succsRaw: parseLinks(a.Successors),
            order: num(a.Sort_Order) ?? i,
        };
    });

    const byId = new Map();
    rows.forEach((r) => { if (!byId.has(r.id)) byId.set(r.id, r); });

    // Depth: the explicit parent chain when there is one, else the level the
    // export stated, else how deep the WBS breadcrumb goes.
    const depthOf = (r, guard = 0) => {
        if (r.parent && byId.has(r.parent) && guard < 40) {
            const p = byId.get(r.parent);
            return p === r ? 0 : depthOf(p, guard + 1) + 1;
        }
        if (r.level) return Math.max(0, r.level - 1);
        return r.wbs ? r.wbs.split(' > ').length : 0;
    };
    rows.forEach((r) => { r.depth = depthOf(r); });

    // A summary row is one something else hangs under. Where no parent ids were
    // exported, the next row being deeper is the same statement.
    const parented = new Set(rows.map((r) => r.parent).filter(Boolean));
    rows.forEach((r, i) => {
        const next = rows[i + 1];
        r.group = parented.has(r.id) || (!parented.size && !!next && next.depth > r.depth);
    });

    // ── the date axis, in calendar days from the first activity ─────────────
    const allDates = rows.flatMap((r) => [r.start, r.finish]).filter(Boolean).sort();
    const origin = allDates[0];
    const dayOf = (d) => (d ? moment(d).diff(moment(origin), 'days') : null);
    rows.forEach((r) => {
        r.es = dayOf(r.start) ?? 0;
        r.ef = Math.max(dayOf(r.finish) ?? 0, r.es);
        r.span = r.ef - r.es + 1;
        // The export's own duration is the scheduler's working days; the span is
        // calendar days. Show theirs when they gave one.
        if (r.days === null) r.days = r.span;
    });

    // ── relationships ──────────────────────────────────────────────────────
    //
    // Every edge is stated once, on the successor's Predecessors cell, because
    // that is where the type and the lag live. The Successors cell is a display
    // of the same graph read the other way, so it is rebuilt rather than
    // trusted — an export can list one and not the other.
    rows.forEach((r) => { r.succs = []; });
    rows.forEach((r) => {
        r.preds = r.preds.filter((l) => byId.has(l.id) && byId.get(l.id) !== r);
        r.preds.forEach((l) => {
            byId.get(l.id).succs.push({ id: r.id, type: l.type, lag: l.lag });
        });
    });
    // An export that listed successors but no predecessors still has a graph;
    // read it the other way so those rows aren't all reported as dangling.
    rows.forEach((r) => {
        r.succsRaw.forEach((l) => {
            const t = byId.get(l.id);
            if (!t || t === r) return;
            if (r.succs.some((s) => s.id === l.id)) return;
            r.succs.push({ id: l.id, type: l.type, lag: l.lag });
            if (!t.preds.some((p) => p.id === r.id)) t.preds.push({ id: r.id, type: l.type, lag: l.lag });
        });
    });

    // ── backward pass → total float ────────────────────────────────────────
    const acts = rows.filter((r) => !r.group);
    const projectFinish = acts.reduce((m, r) => Math.max(m, r.ef), 0);
    const deadline = contractISO ? dayOf(iso(contractISO)) : null;
    // Only open ends answer to the contract date; everything else answers to
    // its successors, which is what makes a late branch read as negative float
    // instead of colouring the whole schedule red.
    acts.forEach((r) => {
        r.lf = deadline !== null && r.succs.length === 0
            ? Math.min(projectFinish, deadline)
            : projectFinish;
    });
    for (let pass = 0; pass < 6; pass += 1) {
        for (let i = acts.length - 1; i >= 0; i -= 1) {
            const t = acts[i];
            let lf = deadline !== null && t.succs.length === 0
                ? Math.min(projectFinish, deadline)
                : projectFinish;
            t.succs.forEach((s) => {
                const q = byId.get(s.id);
                if (!q || q.group || q.lf === undefined) return;
                const qls = q.lf - q.span + 1;
                let v;
                if (s.type === 'FS') v = qls - 1 - s.lag;
                else if (s.type === 'SS') v = qls - s.lag + t.span - 1;
                else if (s.type === 'FF') v = q.lf - s.lag;
                else v = q.lf - s.lag + t.span - 1;
                if (v < lf) lf = v;
            });
            t.lf = lf;
        }
    }
    acts.forEach((r) => {
        // An imported float is the scheduler's own answer and outranks ours.
        r.float = r.importedFloat !== null ? r.importedFloat : r.lf - r.ef;
        r.critical = r.float <= 0;
    });

    // Summary rows roll up: the span of everything beneath, and the tightest
    // float any of it carries.
    const kidsOf = (r) => {
        const out = [];
        const walk = (id) => {
            rows.forEach((c) => {
                if (c.parent === id) { out.push(c); walk(c.id); }
            });
        };
        if (parented.size) walk(r.id);
        else {
            const i = rows.indexOf(r);
            for (let j = i + 1; j < rows.length && rows[j].depth > r.depth; j += 1) out.push(rows[j]);
        }
        return out;
    };
    rows.filter((r) => r.group).forEach((r) => {
        const kids = kidsOf(r).filter((k) => !k.group);
        r.kids = kids.map((k) => k.id);
        r.descendants = kidsOf(r).length;
        if (kids.length) {
            r.float = Math.min(...kids.map((k) => k.float));
            r.critical = kids.some((k) => k.critical);
        } else {
            r.float = 0;
            r.critical = false;
        }
    });

    return {
        rows, byId, origin, projectFinish, deadline,
        totalDays: Math.max(projectFinish + 1, 1),
        finishISO: moment(origin).add(projectFinish, 'days').format('YYYY-MM-DD'),
        acts,
    };
};

/**
 * What is wrong with an activity, in the three buckets the toolbar counts:
 * data the mapping needs, logic the schedule is missing, and dates that don't
 * hold against the contract.
 */
const issuesOf = (r, isLast) => {
    const out = [];
    if (r.group) return out;
    if (!r.code) out.push(['data', 'Missing cost code']);
    if (r.qty === null || r.qty === 0) out.push(['data', 'Missing quantity']);
    if (!r.preds.length && !r.succs.length) out.push(['logic', 'Orphan activity — no predecessor or successor']);
    else {
        if (!r.preds.length) out.push(['logic', 'Open start — no predecessor']);
        if (!r.succs.length && !isLast) out.push(['logic', 'Dangling logic — no successor']);
    }
    if (r.float < 0) out.push(['dates', `Negative float ${r.float}d against the contract date`]);
    return out;
};

// ── ticks ───────────────────────────────────────────────────────────────────

const buildTicks = (origin, totalDays, zoom, px) => {
    const start = moment(origin);
    const end = moment(origin).add(totalDays - 1, 'days');
    const xOf = (m) => Math.round(m.diff(start, 'days') * px);
    const band = (unit, fmt) => {
        const out = [];
        for (let t = start.clone().startOf(unit); t.isSameOrBefore(end); t.add(1, unit)) {
            const from = moment.max(t.clone(), start.clone());
            const to = moment.min(t.clone().endOf(unit), end.clone());
            out.push({
                key: t.format('YYYY-MM-DD'),
                label: typeof fmt === 'function' ? fmt(t) : t.format(fmt),
                left: xOf(from),
                w: Math.max(1, xOf(to) - xOf(from) + Math.round(px)),
            });
        }
        return out;
    };
    if (zoom === 'month') {
        return {
            top: band('quarter', (t) => `${t.format('YYYY')} Q${t.quarter()}`),
            sub: band('month', 'MMM'),
            grid: band('month', 'MMM'),
        };
    }
    if (zoom === 'week') {
        return { top: band('month', "MMM 'YY"), sub: band('week', 'M/D'), grid: band('week', 'M/D') };
    }
    return { top: band('month', "MMM 'YY"), sub: band('day', 'D'), grid: band('week', 'M/D') };
};

// ── chrome ──────────────────────────────────────────────────────────────────

const CSS = `
.sbr, .sbr * { font-family: 'IBM Plex Sans', system-ui, -apple-system, sans-serif; box-sizing: border-box; }
.sbr input, .sbr select, .sbr button { font-family: inherit; }
.sbr .sbr-cell { border: 0; outline: none; background: transparent; border-radius: 3px; padding: 3px; width: 100%; }
.sbr .sbr-cell:hover { background: #EBF0F5; }
.sbr .sbr-cell:focus { background: #fff; box-shadow: inset 0 0 0 1.5px ${ACCENT}; }
.sbr .sbr-cell[readonly] { cursor: default; }
.sbr .sbr-cell[readonly]:hover { background: transparent; }
.sbr .sbr-tool { cursor: pointer; }
.sbr .sbr-tool:hover { background: #F1F4F7; }
.sbr .sbr-dark:hover { background: #1F2733; }
.sbr .sbr-primary:hover { background: ${ACCENT_DK}; }
.sbr .sbr-scroll::-webkit-scrollbar { width: 11px; height: 11px; }
.sbr .sbr-scroll::-webkit-scrollbar-thumb { background: #C3C8D0; border-radius: 6px; border: 3px solid #F7F8FA; }
.sbr .sbr-scroll::-webkit-scrollbar-track { background: #F7F8FA; }
.sbr input[type="date"]::-webkit-calendar-picker-indicator { opacity: .25; }
`;

const headCell = (w, align) => ({
    flex: `0 0 ${w}px`, padding: '0 6px 7px', textAlign: align || 'left',
});

const toolBtn = {
    height: 26, display: 'flex', alignItems: 'center', gap: 6, padding: '0 9px',
    border: `1px solid #CFD5DD`, background: '#fff', borderRadius: 5,
    fontSize: 11.5, cursor: 'pointer', color: INK, whiteSpace: 'nowrap',
};

// ── component ───────────────────────────────────────────────────────────────

const ScheduleBaselineReview = ({
    activities, loading, mappingRows, onAssign, onQty,
    activeImport, onFile, busy, uploadError, uploadNotice,
    onReview, contractDate, jobLabel,
}) => {
    const [q, setQ] = useState('');
    const [codeF, setCodeF] = useState('all');
    const [critOnly, setCritOnly] = useState(false);
    const [issueF, setIssueF] = useState('all');
    const [zoom, setZoom] = useState('week');
    const [logicOpen, setLogicOpen] = useState(true);
    const [collapsed, setCollapsed] = useState(() => new Set());
    const [sel, setSel] = useState(null);
    const [trace, setTrace] = useState(null);
    const [showImport, setShowImport] = useState(false);
    const [status, setStatus] = useState('');

    const leftRef = useRef(null);
    const leftHeadRef = useRef(null);
    const headRef = useRef(null);
    const bodyRef = useRef(null);
    const syncing = useRef(false);

    const model = useMemo(
        () => buildModel(activities, contractDate),
        [activities, contractDate],
    );

    // A different schedule invalidates every selection made against the last one.
    useEffect(() => {
        setCollapsed(new Set());
        setSel(null);
        setTrace(null);
        setStatus('');
    }, [activeImport?.Import_ID]);

    // What phase each activity is mapped to, straight off the mapping panel's
    // live rows, so the grid and the mapping can never disagree.
    const phaseByActivity = useMemo(() => {
        const m = new Map();
        (mappingRows || []).forEach((r) => (r.boundKeys || []).forEach((k) => m.set(k, r)));
        return m;
    }, [mappingRows]);

    const phaseOptions = useMemo(
        () => [...(mappingRows || [])].sort((a, b) => String(a.costCode).localeCompare(String(b.costCode))),
        [mappingRows],
    );

    const phaseOf = useCallback(
        (r) => phaseByActivity.get(activityKey(r.activity)) || null,
        [phaseByActivity],
    );

    // The grid's cost code and quantity are the mapping's, falling back to what
    // the schedule file itself carried.
    const rowsWithMapping = useMemo(() => {
        if (!model) return [];
        return model.rows.map((r) => {
            const ph = phaseOf(r);
            return {
                ...r,
                code: ph?.costCode || r.code,
                qty: ph && has(ph.qty) ? num(ph.qty) : r.qty,
                uom: ph?.uom || r.uom,
                phaseDesc: ph?.description || '',
                guessed: ph?.matchedBy === 'name',
                mapped: !!ph,
            };
        });
    }, [model, phaseOf]);

    const lastActId = model?.acts.length ? model.acts[model.acts.length - 1].id : null;

    const issueMap = useMemo(() => {
        const m = new Map();
        rowsWithMapping.forEach((r) => m.set(r.id, issuesOf(r, r.id === lastActId)));
        return m;
    }, [rowsWithMapping, lastActId]);

    const counts = useMemo(() => {
        const c = { data: 0, logic: 0, dates: 0 };
        issueMap.forEach((list) => list.forEach(([k]) => { c[k] += 1; }));
        return c;
    }, [issueMap]);

    const costCodes = useMemo(() => {
        const seen = new Map();
        rowsWithMapping.forEach((r) => {
            if (r.code && !seen.has(r.code)) seen.set(r.code, r.phaseDesc || '');
        });
        return [...seen.entries()].sort((a, b) => a[0].localeCompare(b[0]));
    }, [rowsWithMapping]);

    // ── filtering ──────────────────────────────────────────────────────────
    const shown = useMemo(() => {
        if (!model) return [];
        const needle = q.trim().toLowerCase();
        const keep = new Set();
        rowsWithMapping.forEach((r) => {
            if (r.group) return;
            let ok = true;
            if (needle) {
                ok = `${r.desc} ${r.code} ${r.id} ${r.wbs}`.toLowerCase().includes(needle);
            }
            if (ok && codeF !== 'all') ok = r.code === codeF;
            if (ok && critOnly) ok = !!r.critical;
            if (ok && issueF !== 'all') ok = (issueMap.get(r.id) || []).some(([k]) => k === issueF);
            if (ok && trace) ok = trace.ids.includes(r.id);
            if (ok) keep.add(r.id);
        });
        // A collapsed summary hides its subtree; a summary with nothing left in
        // it after filtering hides itself.
        const hidden = (r) => {
            let p = r.parent ? model.byId.get(r.parent) : null;
            const guard = new Set();
            while (p && !guard.has(p.id)) {
                guard.add(p.id);
                if (collapsed.has(p.id)) return true;
                p = p.parent ? model.byId.get(p.parent) : null;
            }
            return false;
        };
        return rowsWithMapping.filter((r) => {
            if (trace) return keep.has(r.id);
            if (r.group) return (r.kids || []).some((k) => keep.has(k)) && !hidden(r);
            return keep.has(r.id) && !hidden(r);
        }).slice(0, MAX_ROWS);
    }, [model, rowsWithMapping, q, codeF, critOnly, issueF, trace, collapsed, issueMap]);

    // ── geometry ───────────────────────────────────────────────────────────
    const px = ZOOM[zoom];
    const ganttW = model ? Math.max(Math.round(model.totalDays * px), 320) : 320;
    const ticks = useMemo(
        () => (model ? buildTicks(model.origin, model.totalDays, zoom, px) : null),
        [model, zoom, px],
    );
    const leftW = GRID_W + (logicOpen ? LOGIC_W : LOGIC_SHUT_W);
    const deadlineLeft = model && model.deadline !== null
        ? Math.round((model.deadline + 1) * px)
        : null;

    // ── scroll sync ────────────────────────────────────────────────────────
    const onBodyScroll = useCallback(() => {
        if (syncing.current) return;
        syncing.current = true;
        const b = bodyRef.current;
        if (b) {
            if (headRef.current) headRef.current.scrollLeft = b.scrollLeft;
            if (leftRef.current && leftRef.current.scrollTop !== b.scrollTop) {
                leftRef.current.scrollTop = b.scrollTop;
            }
        }
        syncing.current = false;
    }, []);

    const onLeftScroll = useCallback(() => {
        if (syncing.current) return;
        syncing.current = true;
        const l = leftRef.current;
        if (l) {
            if (leftHeadRef.current) leftHeadRef.current.scrollLeft = l.scrollLeft;
            if (bodyRef.current && bodyRef.current.scrollTop !== l.scrollTop) {
                bodyRef.current.scrollTop = l.scrollTop;
            }
        }
        syncing.current = false;
    }, []);

    // ── actions ────────────────────────────────────────────────────────────
    const groupIds = useMemo(() => rowsWithMapping.filter((r) => r.group).map((r) => r.id), [rowsWithMapping]);
    const allCollapsed = groupIds.length > 0 && groupIds.every((id) => collapsed.has(id));

    const toggleAll = () => {
        setCollapsed(allCollapsed ? new Set() : new Set(groupIds));
        setStatus(allCollapsed ? 'All phases expanded' : 'All phases collapsed to summary bars');
    };

    const toggleGroup = (id) => setCollapsed((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });

    const doTrace = (r, kind) => {
        if (r.group) return;
        if (trace && trace.root === r.id && trace.kind === kind) {
            setTrace(null);
            setStatus('Logic trace cleared');
            return;
        }
        const p = r.preds.map((l) => l.id);
        const s = r.succs.map((l) => l.id);
        const ids = kind === 'preds' ? [r.id, ...p] : kind === 'succs' ? [r.id, ...s] : [r.id, ...p, ...s];
        const label = kind === 'preds' ? 'predecessors' : kind === 'succs' ? 'successors' : 'predecessors + successors';
        setTrace({ root: r.id, kind, ids: [...new Set(ids)], label });
        setStatus(`Tracing ${r.id} and its ${label} — ${ids.length - 1} linked ${ids.length === 2 ? 'activity' : 'activities'}`);
    };

    const totalIssues = counts.data + counts.logic + counts.dates;
    const overContract = model && model.deadline !== null && model.projectFinish > model.deadline;

    // ── empty state ────────────────────────────────────────────────────────
    if (!model) {
        return (
            <div className="sbr" style={{ background: SHELL, borderRadius: 8, border: `1px solid ${LINE}`, overflow: 'hidden' }}>
                <style>{CSS}</style>
                <Header
                    jobLabel={jobLabel}
                    activeImport={activeImport}
                    contractDate={contractDate}
                    count={0}
                    onImport={() => setShowImport(true)}
                    onReview={onReview}
                    reviewable={false}
                />
                <div style={{
                    background: '#fff', padding: '56px 24px', textAlign: 'center',
                }}
                >
                    {loading ? (
                        <div style={{ width: '60%', margin: '0 auto', display: 'grid', rowGap: 8 }}>
                            {[0.9, 0.55, 0.75, 0.4].map((w, i) => (
                                <div key={w} style={{ height: 8, width: `${w * 100}%`, borderRadius: 4, background: '#E2E8F0', marginLeft: `${i * 6}%` }} />
                            ))}
                        </div>
                    ) : (
                        <>
                            <div style={{ fontSize: 13, fontWeight: 600, color: INK_DIM }}>No schedule to review</div>
                            <div style={{ fontSize: 12, color: FAINT, maxWidth: 420, margin: '6px auto 16px', lineHeight: '18px' }}>
                                Import the scheduler’s export — P6 .xer, P6 or MS Project XML, or the
                                schedule sheet — and its activities, logic and quantities appear here on
                                one date axis.
                            </div>
                            <button
                                type="button"
                                className="sbr-primary"
                                onClick={() => setShowImport(true)}
                                style={{ border: 0, background: ACCENT, color: '#fff', fontSize: 11.5, fontWeight: 600, padding: '8px 14px', borderRadius: 5, cursor: 'pointer' }}
                            >
                                Import schedule
                            </button>
                        </>
                    )}
                </div>
                {showImport && (
                    <ImportModal
                        onClose={() => setShowImport(false)}
                        onFile={onFile}
                        busy={busy}
                        error={uploadError}
                        notice={uploadNotice}
                    />
                )}
            </div>
        );
    }

    // ── the workspace ──────────────────────────────────────────────────────
    return (
        <div
            className="sbr"
            style={{
                display: 'flex', flexDirection: 'column', background: SHELL, color: INK,
                fontSize: 12, height: 'calc(100vh - 190px)', minHeight: 520,
                borderRadius: 8, overflow: 'hidden', border: `1px solid ${LINE}`,
            }}
        >
            <style>{CSS}</style>

            <Header
                jobLabel={jobLabel}
                activeImport={activeImport}
                contractDate={contractDate}
                count={model.acts.length}
                onImport={() => setShowImport(true)}
                onReview={onReview}
                reviewable
            />

            {/* Toolbar — structure on the left, what you are looking at in the
                middle, what is wrong with it on the right. */}
            <div style={{
                height: 44, flex: '0 0 44px', display: 'flex', alignItems: 'center', gap: 10,
                padding: '0 14px', background: '#FBFCFD', borderBottom: '1px solid #D8DDE4', overflowX: 'auto',
            }}
            >
                <button type="button" className="sbr-tool" onClick={toggleAll} title="Expand or collapse every phase" style={toolBtn}>
                    {`${allCollapsed ? '▸' : '▾'} ${allCollapsed ? 'Expand all' : 'Collapse all'}`}
                </button>
                <div style={{ width: 1, height: 22, background: '#DEE3E9', flex: '0 0 1px' }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, height: 26, padding: '0 8px', border: '1px solid #CFD5DD', borderRadius: 5, background: '#fff', width: 190, flex: '0 0 190px' }}>
                    <span style={{ color: GHOST, fontSize: 11 }}>⌕</span>
                    <input
                        type="text"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search activity, cost code, WBS…"
                        style={{ border: 0, outline: 'none', background: 'transparent', fontSize: 11.5, width: '100%', color: INK }}
                    />
                </div>
                <select
                    value={codeF}
                    onChange={(e) => setCodeF(e.target.value)}
                    style={{ height: 26, border: '1px solid #CFD5DD', borderRadius: 5, background: '#fff', fontSize: 11.5, padding: '0 6px', color: INK, maxWidth: 210 }}
                >
                    <option value="all">All cost codes</option>
                    {costCodes.map(([code, desc]) => (
                        <option key={code} value={code}>{desc ? `${code} · ${desc}` : code}</option>
                    ))}
                </select>
                <button
                    type="button"
                    onClick={() => setCritOnly((v) => !v)}
                    style={{
                        ...toolBtn,
                        border: `1px solid ${critOnly ? CRIT : '#CFD5DD'}`,
                        background: critOnly ? CRIT_BG : '#fff',
                        color: critOnly ? CRIT_DK : INK_DIM,
                    }}
                >
                    <span style={{ width: 8, height: 8, borderRadius: 2, background: CRIT }} />
                    Critical only
                </button>
                <div style={{ width: 1, height: 22, background: '#DEE3E9', flex: '0 0 1px' }} />
                <div style={{ display: 'flex', border: '1px solid #CFD5DD', borderRadius: 5, overflow: 'hidden', height: 26, background: '#fff', flex: '0 0 auto' }}>
                    {['day', 'week', 'month'].map((z) => (
                        <button
                            key={z}
                            type="button"
                            onClick={() => setZoom(z)}
                            style={{
                                border: 0, borderRight: '1px solid #E3E7EC',
                                background: zoom === z ? ACCENT_BG : '#fff',
                                color: zoom === z ? ACCENT_DK : MUTED,
                                fontSize: 11, fontWeight: zoom === z ? 600 : 400,
                                padding: '0 11px', cursor: 'pointer', textTransform: 'capitalize',
                            }}
                        >
                            {z}
                        </button>
                    ))}
                </div>
                <button
                    type="button"
                    onClick={() => setLogicOpen((v) => !v)}
                    title="Show sequencing, predecessors and successors"
                    style={{
                        ...toolBtn,
                        border: `1px solid ${logicOpen ? ACCENT : '#CFD5DD'}`,
                        background: logicOpen ? ACCENT_BG : '#fff',
                        color: logicOpen ? ACCENT_DK : INK_DIM,
                    }}
                >
                    {`${logicOpen ? '▾' : '▸'} Logic`}
                </button>
                <div style={{ flex: 1, minWidth: 8 }} />
                {trace && (
                    <div
                        title={`Filtered to ${trace.root} and its ${trace.label} (${trace.ids.length} rows) — double-click the cell again or ✕ to clear`}
                        style={{
                            height: 26, display: 'flex', alignItems: 'center', gap: 6, padding: '0 5px 0 8px',
                            border: `1px solid ${ACCENT}`, background: ACCENT_BG, borderRadius: 5,
                            fontSize: 11.5, color: ACCENT_DK, whiteSpace: 'nowrap', flex: '0 0 auto',
                        }}
                    >
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: ACCENT, flex: '0 0 6px' }} />
                        {`Trace ${trace.root} · ${trace.kind === 'preds' ? 'pred' : trace.kind === 'succs' ? 'succ' : 'both'}`}
                        <span style={{ fontFamily: MONO, fontSize: 10.5, color: '#3A7A94' }}>{`${trace.ids.length}r`}</span>
                        <button
                            type="button"
                            onClick={() => { setTrace(null); setStatus('Logic trace cleared'); }}
                            title="Clear logic trace"
                            style={{ border: 0, background: 'transparent', color: ACCENT_DK, fontSize: 12, cursor: 'pointer', padding: '0 2px' }}
                        >
                            ✕
                        </button>
                    </div>
                )}
                {[['data', 'Missing data', CRIT], ['logic', 'Logic gaps', WARN], ['dates', 'Dates & float', DATE_ISSUE]].map(([key, label, dot]) => (
                    <button
                        key={key}
                        type="button"
                        onClick={() => setIssueF((v) => (v === key ? 'all' : key))}
                        style={{
                            ...toolBtn,
                            border: `1px solid ${issueF === key ? dot : '#CFD5DD'}`,
                            background: issueF === key ? '#FFF4F0' : '#fff',
                            flex: '0 0 auto',
                        }}
                    >
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: dot }} />
                        {label}
                        <span style={{ fontFamily: MONO, fontWeight: 600, color: dot }}>{counts[key]}</span>
                    </button>
                ))}
            </div>

            {/* Panes */}
            <div style={{ flex: 1, minHeight: 0, display: 'flex', background: '#fff' }}>
                {/* Left: the grid */}
                <div style={{
                    flex: `1 1 ${leftW}px`, maxWidth: leftW, minWidth: 340,
                    display: 'flex', flexDirection: 'column', borderRight: `2px solid ${LINE}`,
                }}
                >
                    <div ref={leftHeadRef} style={{ height: AXIS_H, flex: `0 0 ${AXIS_H}px`, overflow: 'hidden', background: HEAD_BG, borderBottom: `1px solid ${LINE}` }}>
                        <div style={{
                            width: leftW, height: AXIS_H, display: 'flex', alignItems: 'flex-end',
                            fontSize: 10, fontWeight: 600, letterSpacing: '.4px', textTransform: 'uppercase', color: MUTED,
                        }}
                        >
                            <div style={headCell(COL.id, 'right')}>ID</div>
                            <div style={headCell(COL.code)}>Cost Code</div>
                            <div style={headCell(COL.desc)}>Activity Description</div>
                            <div style={headCell(COL.start)}>Start</div>
                            <div style={headCell(COL.finish)}>Finish</div>
                            <div style={headCell(COL.days, 'right')}>Days</div>
                            <div style={headCell(COL.qty, 'right')}>Quantity</div>
                            <div style={headCell(COL.uom)}>UoM</div>
                            <div style={headCell(COL.float, 'right')}>Float</div>
                            {logicOpen ? (
                                <div style={{ flex: `0 0 ${LOGIC_W}px`, display: 'flex', flexDirection: 'column', height: '100%', background: '#E9EDF2', borderLeft: `1px solid ${LINE}` }}>
                                    <div style={{ height: 19, display: 'flex', alignItems: 'center', gap: 5, padding: '0 6px', color: '#40525F', borderBottom: '1px solid #D5DCE4' }}>
                                        <button type="button" onClick={() => setLogicOpen(false)} style={{ border: 0, background: 'transparent', cursor: 'pointer', color: '#40525F', fontSize: 9, padding: 0 }}>▾</button>
                                        <span>Logic &amp; Sequencing</span>
                                    </div>
                                    <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end' }}>
                                        <div style={headCell(56)}>Seq</div>
                                        <div style={headCell(92)}>Predecessors</div>
                                        <div style={headCell(92)}>Successors</div>
                                    </div>
                                </div>
                            ) : (
                                <div style={{ flex: `0 0 ${LOGIC_SHUT_W}px`, height: '100%', background: '#E9EDF2', borderLeft: `1px solid ${LINE}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <button type="button" onClick={() => setLogicOpen(true)} title="Expand sequencing, predecessors, successors" style={{ border: 0, background: 'transparent', cursor: 'pointer', color: '#40525F', fontSize: 11 }}>▸</button>
                                </div>
                            )}
                        </div>
                    </div>

                    <div ref={leftRef} className="sbr-scroll" onScroll={onLeftScroll} style={{ flex: 1, overflow: 'auto' }}>
                        <div style={{ width: leftW }}>
                            {shown.map((r) => (
                                <GridRow
                                    key={r.key}
                                    row={r}
                                    issues={issueMap.get(r.id) || []}
                                    selected={sel === r.id}
                                    collapsed={collapsed.has(r.id)}
                                    logicOpen={logicOpen}
                                    traced={trace?.root === r.id}
                                    phaseOptions={phaseOptions}
                                    onSelect={() => setSel(r.id)}
                                    onToggle={() => toggleGroup(r.id)}
                                    onAssign={onAssign}
                                    onQty={onQty}
                                    onTrace={doTrace}
                                />
                            ))}
                            <div style={{ height: 120 }} />
                        </div>
                    </div>
                </div>

                {/* Right: the Gantt */}
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', background: '#FDFDFE' }}>
                    <div ref={headRef} style={{ height: AXIS_H, flex: `0 0 ${AXIS_H}px`, overflow: 'hidden', background: HEAD_BG, borderBottom: `1px solid ${LINE}` }}>
                        <div style={{ position: 'relative', width: ganttW, height: AXIS_H }}>
                            {ticks.top.map((t) => (
                                <div
                                    key={`t${t.key}`}
                                    style={{
                                        position: 'absolute', top: 0, left: t.left, width: t.w, height: 21,
                                        borderRight: '1px solid #D5DCE4', borderBottom: '1px solid #D5DCE4',
                                        fontSize: 10, fontWeight: 600, color: '#40525F', display: 'flex',
                                        alignItems: 'center', paddingLeft: 6, overflow: 'hidden', whiteSpace: 'nowrap',
                                    }}
                                >
                                    {t.label}
                                </div>
                            ))}
                            {ticks.sub.map((t) => (
                                <div
                                    key={`s${t.key}`}
                                    style={{
                                        position: 'absolute', top: 21, left: t.left, width: t.w, height: 23,
                                        borderRight: '1px solid #E1E6EC', fontSize: 9.5, color: FAINT,
                                        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
                                        paddingBottom: 5, overflow: 'hidden', whiteSpace: 'nowrap',
                                    }}
                                >
                                    {t.label}
                                </div>
                            ))}
                            {deadlineLeft !== null && (
                                <div style={{ position: 'absolute', top: 0, left: deadlineLeft, height: AXIS_H, display: 'flex', alignItems: 'flex-start' }}>
                                    <div style={{ width: 0, height: AXIS_H, borderLeft: `1.5px dashed ${CRIT}` }} />
                                    <span style={{ fontSize: 9, fontWeight: 600, color: CRIT, background: CRIT_BG, padding: '1px 4px', borderRadius: '0 3px 3px 0', whiteSpace: 'nowrap' }}>
                                        CONTRACT MC
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div ref={bodyRef} className="sbr-scroll" onScroll={onBodyScroll} style={{ flex: 1, overflow: 'auto' }}>
                        <div style={{ position: 'relative', width: ganttW, height: Math.max(shown.length * ROW_H + 120, 320) }}>
                            {ticks.grid.map((g, i) => (
                                <div
                                    key={`g${g.key}`}
                                    style={{
                                        position: 'absolute', top: 0, bottom: 0, left: g.left, width: g.w,
                                        borderLeft: `1px solid ${RULE}`,
                                        background: i % 2 ? 'rgba(237,240,243,.35)' : 'transparent',
                                    }}
                                />
                            ))}
                            {deadlineLeft !== null && (
                                <div style={{ position: 'absolute', top: 0, bottom: 0, left: deadlineLeft, borderLeft: `1.5px dashed ${CRIT}` }} />
                            )}
                            {shown.map((r, i) => (
                                <GanttRow
                                    key={r.key}
                                    row={r}
                                    top={i * ROW_H}
                                    px={px}
                                    selected={sel === r.id}
                                    issues={issueMap.get(r.id) || []}
                                    onSelect={() => setSel(r.id)}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Status bar */}
            <div style={{
                height: 34, flex: '0 0 34px', display: 'flex', alignItems: 'center', gap: 18,
                padding: '0 16px', background: HEAD_BG, borderTop: `1px solid ${LINE}`,
                fontSize: 11, color: MUTED, overflow: 'hidden',
            }}
            >
                <span style={{ whiteSpace: 'nowrap' }}>
                    <span style={{ color: INK, fontWeight: 600 }}>{intFmt(model.acts.length)}</span>
                    {' activities · '}
                    <span style={{ color: INK, fontWeight: 600 }}>{intFmt(model.rows.length - model.acts.length)}</span>
                    {' summary rows'}
                </span>
                <span style={{ whiteSpace: 'nowrap' }}>
                    {'Starts '}
                    <span style={{ fontFamily: MONO, color: INK }}>{fmtDate(model.origin)}</span>
                    {' → finishes '}
                    <span style={{ fontFamily: MONO, color: overContract ? CRIT : INK, fontWeight: 600 }}>
                        {fmtDate(model.finishISO)}
                    </span>
                    {` · ${intFmt(model.totalDays)} days`}
                </span>
                <span style={{ whiteSpace: 'nowrap' }}>
                    <span style={{ color: CRIT, fontWeight: 600 }}>{intFmt(model.acts.filter((r) => r.critical).length)}</span>
                    {' activities on the critical path'}
                </span>
                <span style={{ flex: 1 }} />
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {status || (critOnly ? 'Filtered to the critical path' : 'Double-click a predecessor or successor cell to trace the logic')}
                </span>
                <span style={{
                    padding: '2px 8px', borderRadius: 10, fontWeight: 600, fontSize: 10.5, whiteSpace: 'nowrap',
                    background: totalIssues ? CRIT_BG : GOOD_BG, color: totalIssues ? CRIT_DK : GOOD,
                }}
                >
                    {totalIssues ? `${intFmt(totalIssues)} issues to clear before baseline` : 'Ready to baseline'}
                </span>
            </div>

            {showImport && (
                <ImportModal
                    onClose={() => setShowImport(false)}
                    onFile={onFile}
                    busy={busy}
                    error={uploadError}
                    notice={uploadNotice}
                />
            )}
        </div>
    );
};

// ── header ──────────────────────────────────────────────────────────────────

const Header = ({ jobLabel, activeImport, contractDate, count, onImport, onReview, reviewable }) => {
    const source = String(activeImport?.Source || '').toUpperCase() || 'FILE';
    const uploaded = activeImport?.Uploaded_At
        ? moment.utc(activeImport.Uploaded_At).format('MMM D, YYYY')
        : null;
    return (
        <div style={{
            height: 52, flex: '0 0 52px', display: 'flex', alignItems: 'center', gap: 14,
            padding: '0 16px', background: '#141A22', color: SHELL, borderBottom: '1px solid #000',
        }}
        >
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15, flex: '0 0 auto' }}>
                <span style={{ fontSize: 13, fontWeight: 600, letterSpacing: '.2px' }}>Schedule Baseline Review</span>
                <span style={{ fontSize: 10.5, color: '#8D99A8', letterSpacing: '.3px', textTransform: 'uppercase', whiteSpace: 'nowrap', maxWidth: 420, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {jobLabel || '—'}
                </span>
            </div>
            <div style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px',
                border: '1px solid #2C3642', borderRadius: 5, background: '#1B2330', minWidth: 0,
            }}
            >
                <span style={{ width: 16, height: 16, borderRadius: 3, background: '#1D6F42', color: '#fff', fontSize: 8, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 16px' }}>
                    {source.slice(0, 3)}
                </span>
                <span style={{ fontFamily: MONO, fontSize: 11, color: '#D7DEE7', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 240 }}>
                    {activeImport?.File_Name || 'No schedule imported'}
                </span>
                <span style={{ fontSize: 10.5, color: '#7C8899', whiteSpace: 'nowrap' }}>
                    {activeImport
                        ? `imported ${uploaded || '—'} · ${intFmt(count)} activities mapped`
                        : 'nothing imported yet'}
                </span>
                <button
                    type="button"
                    onClick={onImport}
                    style={{ border: 0, background: '#2C3642', color: '#C9D4E0', fontSize: 10.5, padding: '3px 8px', borderRadius: 4, cursor: 'pointer' }}
                >
                    {activeImport ? 'Re-import' : 'Import'}
                </button>
            </div>
            <div style={{ flex: 1 }} />
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.2, marginRight: 4 }}>
                <span style={{ fontSize: 10.5, color: '#8D99A8' }}>Contract mechanical completion</span>
                <span style={{ fontFamily: MONO, fontSize: 11.5, color: contractDate ? '#F0B429' : '#6B7787' }}>
                    {contractDate ? longDate(contractDate) : 'not set'}
                </span>
            </div>
            <button
                type="button"
                className="sbr-dark"
                onClick={onReview}
                disabled={!reviewable}
                title={reviewable ? 'Bind the budget’s cost codes to the schedule' : 'Import a schedule first'}
                style={{
                    border: '1px solid #2C3642', background: 'transparent', color: '#C9D4E0',
                    fontSize: 11.5, fontWeight: 500, padding: '7px 12px', borderRadius: 5,
                    cursor: reviewable ? 'pointer' : 'default', opacity: reviewable ? 1 : 0.5,
                }}
            >
                Review mapping
            </button>
            <button
                type="button"
                className="sbr-primary"
                onClick={onReview}
                disabled={!reviewable}
                style={{
                    border: 0, background: ACCENT, color: '#fff', fontSize: 11.5, fontWeight: 600,
                    padding: '8px 14px', borderRadius: 5,
                    cursor: reviewable ? 'pointer' : 'default', opacity: reviewable ? 1 : 0.5,
                }}
            >
                Set Baseline
            </button>
        </div>
    );
};

// ── grid row ────────────────────────────────────────────────────────────────

const GridRow = ({
    row: r, issues, selected, collapsed, logicOpen, traced,
    phaseOptions, onSelect, onToggle, onAssign, onQty, onTrace,
}) => {
    const negFloat = r.float < 0;
    const zeroFloat = r.float === 0;
    const missingQty = !r.group && (r.qty === null || r.qty === 0);
    const cellMono = { fontFamily: MONO, fontSize: 10.5 };
    const traceFg = traced ? ACCENT_DK : INK_DIM;

    return (
        <div
            onClick={onSelect}
            style={{
                display: 'flex', height: ROW_H, alignItems: 'center',
                background: selected ? ACCENT_BG : r.group ? '#F1F4F7' : issues.length ? '#FEFAF8' : '#fff',
                borderBottom: `1px solid ${r.group ? LINE : RULE}`,
                fontWeight: r.group ? 600 : 400,
            }}
        >
            <div style={{ flex: `0 0 ${COL.id}px`, padding: '0 6px', textAlign: 'right', ...cellMono, color: '#8B96A5' }}>{r.id}</div>

            <div style={{ flex: `0 0 ${COL.code}px`, padding: '0 3px', display: 'flex', alignItems: 'center', gap: 3 }}>
                <span style={{ width: 4, height: 14, borderRadius: 1, background: r.group ? INK_DIM : r.critical ? CRIT : '#C7D2DC', flex: '0 0 4px' }} />
                {r.group ? (
                    <span style={{ ...cellMono, color: INK, padding: 3 }}>{r.code}</span>
                ) : (
                    <select
                        className="sbr-cell"
                        value={r.code}
                        disabled={!onAssign}
                        title={r.phaseDesc || (r.code ? `Cost code ${r.code}` : 'Not mapped to a cost code')}
                        onChange={(e) => onAssign && onAssign(activityKey(r.activity), e.target.value)}
                        style={{
                            ...cellMono,
                            color: r.code ? (r.guessed ? MUTED : ACCENT) : CRIT,
                            appearance: 'none', cursor: onAssign ? 'pointer' : 'default',
                        }}
                    >
                        <option value="">—</option>
                        {phaseOptions.map((p) => (
                            <option key={p.costCode} value={p.costCode}>
                                {p.description ? `${p.costCode} · ${p.description}` : p.costCode}
                            </option>
                        ))}
                        {/* A code the schedule carried that the budget has no row
                            for still has to show, or the cell reads as unmapped. */}
                        {r.code && !phaseOptions.some((p) => p.costCode === r.code) && (
                            <option value={r.code}>{r.code}</option>
                        )}
                    </select>
                )}
            </div>

            <div style={{ flex: `0 0 ${COL.desc}px`, padding: '0 4px 0 0', paddingLeft: (r.group ? 4 : 20) + Math.min(r.depth, 6) * 10, display: 'flex', alignItems: 'center', gap: 3, minWidth: 0 }}>
                {r.group && (
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onToggle(); }}
                        style={{ border: 0, background: 'transparent', cursor: 'pointer', color: MUTED, fontSize: 9, width: 12, padding: 0, flex: '0 0 12px' }}
                    >
                        {collapsed ? '▸' : '▾'}
                    </button>
                )}
                <span
                    title={`${r.desc}${r.wbs ? `\n${r.wbs}` : ''}${r.pipeSize ? `\n${r.pipeSize}` : ''}`}
                    style={{ color: r.group ? '#101720' : INK, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, padding: 3 }}
                >
                    {r.desc}
                </span>
            </div>

            <div style={{ flex: `0 0 ${COL.start}px`, padding: '0 3px' }}>
                <input type="text" className="sbr-cell" readOnly value={fmtDate(r.start)} title="From the imported schedule" style={{ ...cellMono, color: INK_DIM }} />
            </div>
            <div style={{ flex: `0 0 ${COL.finish}px`, padding: '0 3px' }}>
                <input type="text" className="sbr-cell" readOnly value={fmtDate(r.finish)} title="From the imported schedule" style={{ ...cellMono, color: INK_DIM }} />
            </div>
            <div style={{ flex: `0 0 ${COL.days}px`, padding: '0 3px' }}>
                <input type="text" className="sbr-cell" readOnly value={r.days ?? ''} style={{ ...cellMono, textAlign: 'right', color: INK }} />
            </div>

            <div style={{ flex: `0 0 ${COL.qty}px`, padding: '0 3px', display: 'flex', alignItems: 'center', gap: 2 }}>
                <span style={{ fontSize: 10, color: CRIT, width: 7, flex: '0 0 7px' }}>{missingQty ? '⚠' : ''}</span>
                {r.group ? null : (
                    <input
                        type="number"
                        className="sbr-cell"
                        value={r.qty ?? ''}
                        readOnly={!onQty || !r.code}
                        placeholder={r.code ? 'req’d' : ''}
                        title={r.code
                            ? `Quantity for ${r.code} — shared by every activity on this phase`
                            : 'Map a cost code before setting a quantity'}
                        onChange={(e) => onQty && r.code && onQty(r.code, e.target.value === '' ? 0 : Number(e.target.value))}
                        style={{ ...cellMono, textAlign: 'right', color: INK, background: missingQty ? CRIT_BG : 'transparent' }}
                    />
                )}
            </div>

            <div style={{ flex: `0 0 ${COL.uom}px`, padding: '0 2px' }}>
                <span style={{ ...cellMono, color: MUTED, padding: 2, display: 'block' }}>{r.group ? '' : r.uom}</span>
            </div>

            <div style={{
                flex: `0 0 ${COL.float}px`, padding: '0 6px', textAlign: 'right', ...cellMono, fontWeight: 600,
                color: negFloat ? '#fff' : zeroFloat ? CRIT : MUTED,
                background: negFloat ? CRIT : zeroFloat ? CRIT_BG : 'transparent',
                height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
            }}
            >
                {r.group ? '' : `${r.float}d`}
            </div>

            {logicOpen ? (
                <div style={{
                    flex: `0 0 ${LOGIC_W}px`, display: 'flex', alignItems: 'center', height: '100%',
                    background: selected ? '#EAF3F7' : r.group ? '#F1F4F7' : '#F8FAFB',
                    borderLeft: '1px solid #DDE3EA',
                }}
                >
                    <div style={{ flex: '0 0 56px', padding: '0 6px', display: 'flex', gap: 3, overflow: 'hidden' }}>
                        {r.group ? null : [...new Set(r.preds.map(linkLabel))].slice(0, 2).map((t) => {
                            const c = SEQ_COLOR[t.slice(0, 2)] || ['#EDEFF2', MUTED];
                            return (
                                <span key={t} style={{ fontFamily: MONO, fontSize: 9.5, fontWeight: 600, padding: '1.5px 4px', borderRadius: 3, background: c[0], color: c[1], whiteSpace: 'nowrap' }}>
                                    {t}
                                </span>
                            );
                        })}
                    </div>
                    <div
                        onDoubleClick={() => onTrace(r, 'preds')}
                        title="Double-click to filter to this activity and its predecessors"
                        style={{ flex: '0 0 92px', padding: '0 3px', display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer' }}
                    >
                        <span style={{ fontSize: 10, color: CRIT, width: 7, flex: '0 0 7px' }}>{!r.group && !r.preds.length ? '⚠' : ''}</span>
                        <span style={{ fontFamily: MONO, fontSize: 10, color: traceFg, padding: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {r.group ? '' : linkStr(r.preds) || 'none'}
                        </span>
                    </div>
                    <div
                        onDoubleClick={() => onTrace(r, 'succs')}
                        title="Double-click to filter to this activity and its successors"
                        style={{ flex: '0 0 92px', padding: '0 3px', display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer' }}
                    >
                        <span style={{ fontSize: 10, color: CRIT, width: 7, flex: '0 0 7px' }}>{!r.group && !r.succs.length ? '⚠' : ''}</span>
                        <span style={{ fontFamily: MONO, fontSize: 10, color: traceFg, padding: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {r.group ? '' : linkStr(r.succs) || 'none'}
                        </span>
                    </div>
                </div>
            ) : (
                <div
                    onDoubleClick={() => onTrace(r, 'both')}
                    title={`Predecessors: ${linkStr(r.preds) || 'none'} · Successors: ${linkStr(r.succs) || 'none'}`}
                    style={{
                        flex: `0 0 ${LOGIC_SHUT_W}px`, height: '100%',
                        background: selected ? '#EAF3F7' : r.group ? '#F1F4F7' : '#F8FAFB',
                        borderLeft: '1px solid #DDE3EA', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', fontFamily: MONO, fontSize: 9.5, color: traceFg, cursor: 'pointer',
                    }}
                >
                    {r.group ? '' : `${r.preds.length}/${r.succs.length}`}
                </div>
            )}
        </div>
    );
};

// ── gantt row ───────────────────────────────────────────────────────────────

const GanttRow = ({ row: r, top, px, selected, issues, onSelect }) => {
    const left = Math.round(r.es * px);
    const w = Math.max(3, Math.round(r.span * px));
    const showFloat = !r.group && r.float > 0;
    const barH = r.group ? 8 : BAR_H;
    const tip = `${r.desc}\n${longDate(r.start)} → ${longDate(r.finish)}  ·  ${r.days} d  ·  float ${r.float}d`
        + (issues.length ? `\n⚠ ${issues.map(([, m]) => m).join('\n⚠ ')}` : '');

    return (
        <div
            onClick={onSelect}
            style={{
                position: 'absolute', left: 0, right: 0, top, height: ROW_H,
                borderBottom: `1px solid ${r.group ? LINE : RULE}`,
                background: selected ? '#F2F8FB' : r.group ? 'rgba(200,207,216,.18)' : 'transparent',
            }}
        >
            {showFloat && (
                <div style={{
                    position: 'absolute', top: Math.round(ROW_H / 2), left: left + w,
                    width: Math.max(2, Math.round(r.float * px)), height: 0,
                    borderTop: '1.5px dotted #A9B3C0',
                }}
                />
            )}
            <div
                title={tip}
                style={{
                    position: 'absolute', top: Math.round((ROW_H - barH) / 2), left, width: r.milestone ? barH : w,
                    height: barH,
                    background: r.group ? INK_DIM : r.critical ? CRIT : ACCENT,
                    border: `1px solid ${r.group ? INK : r.critical ? CRIT_DK : ACCENT_DK}`,
                    borderRadius: r.group ? 1 : 2,
                    transform: r.milestone ? 'rotate(45deg)' : 'none',
                    overflow: 'hidden',
                }}
            >
                {!r.group && !r.milestone && r.pct > 0 && (
                    <div style={{ width: `${Math.min(100, r.pct)}%`, height: '100%', background: 'rgba(255,255,255,.45)' }} />
                )}
            </div>
            <div style={{
                position: 'absolute', top: 0, height: ROW_H, left: left + w + 6,
                display: 'flex', alignItems: 'center', fontSize: 9.5, color: FAINT,
                whiteSpace: 'nowrap', pointerEvents: 'none',
            }}
            >
                {`${issues.length ? '⚠ ' : ''}${r.days} d`}
            </div>
        </div>
    );
};

// ── import modal ────────────────────────────────────────────────────────────

// The field mapping is the parser's own, not a picker: these sheets are read by
// header name, so what the modal lists is exactly what the reader looks for.
const FIELD_MAP = [
    ['Activity ID / Task ID', 'ID'],
    ['Task Name (indent = outline)', 'Activity Description'],
    ['Cost Code / Phase', 'Cost Code'],
    ['Start / Finish', 'Start / Finish'],
    ['Duration', 'Days'],
    ['Quantity / UOM', 'Quantity / UoM'],
    ['Link Type + Predecessor ID', 'Sequencing + Predecessors'],
    ['Successor ID', 'Successors'],
];

const ImportModal = ({ onClose, onFile, busy, error, notice }) => {
    const inputRef = useRef(null);

    const pick = (file) => {
        if (!file || !onFile) return;
        const ext = String(file.name).toLowerCase().split('.').pop();
        const bad = SCHEDULE_EXT.includes(ext)
            ? null
            : `${file.name} isn’t a schedule export — expected ${SCHEDULE_EXT.join(', ')}.`;
        onFile(file, bad);
    };

    return (
        <div
            style={{
                position: 'fixed', inset: 0, background: 'rgba(20,26,34,.55)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200,
            }}
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{ width: 560, maxWidth: '92vw', background: '#fff', borderRadius: 8, boxShadow: '0 24px 60px rgba(0,0,0,.35)', overflow: 'hidden' }}
            >
                <div style={{ padding: '16px 20px', borderBottom: '1px solid #E3E7EC', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>Import the project schedule</span>
                    <button type="button" onClick={onClose} style={{ border: 0, background: 'transparent', fontSize: 15, color: '#8B96A5', cursor: 'pointer' }}>✕</button>
                </div>
                <div style={{ padding: 20 }}>
                    <div
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer?.files?.[0]); }}
                        style={{ border: '1.5px dashed #C0C8D2', borderRadius: 6, padding: 26, textAlign: 'center', background: '#F8FAFB' }}
                    >
                        <div style={{ fontSize: 12.5, fontWeight: 500, marginBottom: 4 }}>
                            {busy ? 'Reading the schedule…' : 'Drop a .xer, .xml or schedule workbook here'}
                        </div>
                        <div style={{ fontSize: 11, color: FAINT }}>
                            {'Or '}
                            <button
                                type="button"
                                onClick={() => inputRef.current?.click()}
                                style={{ border: 0, background: 'transparent', color: ACCENT, cursor: 'pointer', padding: 0, fontSize: 11, textDecoration: 'underline' }}
                            >
                                browse files
                            </button>
                            {' · P6 .xer, P6 / MS Project XML, or .xlsx — .mpp has to be saved as XML first'}
                        </div>
                        <input
                            ref={inputRef}
                            type="file"
                            accept=".xer,.xml,.xlsx,.xlsm,.xls,.csv"
                            style={{ display: 'none' }}
                            onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ''; }}
                        />
                    </div>
                    {error ? <div style={{ marginTop: 12, fontSize: 11.5, color: CRIT_DK }}>{error}</div> : null}
                    {notice ? <div style={{ marginTop: 12, fontSize: 11.5, color: GOOD }}>{notice}</div> : null}
                    <div style={{ marginTop: 18, fontSize: 10, fontWeight: 600, letterSpacing: '.4px', textTransform: 'uppercase', color: MUTED }}>
                        Field mapping
                    </div>
                    <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: '1fr 16px 1fr', gap: '6px 10px', alignItems: 'center', fontSize: 11.5 }}>
                        {FIELD_MAP.map(([from, to]) => (
                            <React.Fragment key={to}>
                                <span style={{ fontFamily: MONO, fontSize: 10.5, color: INK_DIM, background: HEAD_BG, padding: '5px 7px', borderRadius: 4 }}>{from}</span>
                                <span style={{ color: GHOST, textAlign: 'center' }}>→</span>
                                <span style={{ padding: '5px 7px', border: '1px solid #DDE3EA', borderRadius: 4, color: INK }}>{to}</span>
                            </React.Fragment>
                        ))}
                    </div>
                </div>
                <div style={{ padding: '14px 20px', borderTop: '1px solid #E3E7EC', display: 'flex', justifyContent: 'flex-end', gap: 8, background: '#FBFCFD' }}>
                    <button type="button" onClick={onClose} style={{ border: '1px solid #CFD5DD', background: '#fff', padding: '7px 13px', borderRadius: 5, fontSize: 11.5, cursor: 'pointer' }}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ScheduleBaselineReview;
